import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChatMessage, chatService } from '@/services/chatService';
import { Button } from '@/components/ui';
import { testChatAPI, testStreamingChatAPI } from '@/utils/testChatAPI';
import { useChatSettingsStore } from '@/store/chatSettingsStore';
import styles from '@/styles/components/chat/ChatInterface.module.css';
import { parseThinkingFromText } from '@/utils/thinking';
import { ThinkingPanel } from '@/components/chat/ThinkingPanel';

interface ChatInterfaceProps {
  modelId: string;
  modelName: string;
  onBack?: () => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  modelId,
  modelName,
  onBack,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chatId] = useState(() => chatService.generateChatId());

  // Get chat settings from store
  const temperature = useChatSettingsStore(s => s.temperature);
  const topP = useChatSettingsStore(s => s.topP);
  const topK = useChatSettingsStore(s => s.topK);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Auto-resize textarea functionality
  useEffect(() => {
    const textarea = inputRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height to scrollHeight, but with min and max constraints
      const scrollHeight = textarea.scrollHeight;
      const minHeight = 24; // Single line height
      const maxHeight = 200; // Maximum height before scrolling
      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }, [inputValue]);

  // Load chat history on mount
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const history = await chatService.loadChatHistory(
          'models-gallery',
          chatId
        );
        setMessages(history);
      } catch (error) {
        console.error('Failed to load chat history:', error);
      }
    };

    loadHistory();
  }, [chatId]);

  // Save messages to history only when streaming is complete or interrupted
  useEffect(() => {
    if (messages.length > 0 && !isStreaming) {
      const saveHistory = async () => {
        try {
          await chatService.saveChatHistory('models-gallery', chatId, messages);
        } catch (error) {
          console.error('Failed to save chat history:', error);
        }
      };

      saveHistory();
    }
  }, [messages, chatId, isStreaming]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: chatService.generateMessageId(),
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setIsStreaming(true);
    setError(null);

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    // Create assistant message for streaming
    let assistantMessage: ChatMessage = {
      id: chatService.generateMessageId(),
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      model: { id: modelId },
    };

    try {
      const apiModelName = chatService.getModelNameFromId(modelId);
      const allMessages = [...messages, userMessage];

      setMessages(prev => [...prev, assistantMessage]);

      // Stream the response
      console.log('🚀 Sending chat request');
      const stream = chatService.sendStreamingChatCompletion({
        model: apiModelName,
        messages: allMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
        })),
        stream: true,
        temperature,
        top_p: topP,
        top_k: topK,
      });

      let fullContent = '';
      let updateQueue: string[] = [];
      let isUpdating = false;

      // Smooth streaming update function
      const updateUI = () => {
        if (updateQueue.length > 0 && !isUpdating) {
          isUpdating = true;
          requestAnimationFrame(() => {
            const newContent = updateQueue.join('');
            updateQueue = [];
            fullContent += newContent;

            setMessages(prev =>
              prev.map(msg =>
                msg.id === assistantMessage.id
                  ? { ...msg, content: fullContent }
                  : msg
              )
            );

            // Auto-scroll during streaming for better UX
            scrollToBottom();
            isUpdating = false;
          });
        }
      };

      // If backend emits an id during streaming, adopt it
      let assistantIdSet = false;
      for await (const chunk of stream) {
        if (!assistantIdSet && chunk?.id && assistantMessage) {
          assistantIdSet = true;
          const newId = chunk.id;
          const oldId = assistantMessage.id;
          if (newId && newId !== oldId) {
            assistantMessage = { ...assistantMessage, id: newId };
            setMessages(prev =>
              prev.map(msg => (msg.id === oldId ? { ...msg, id: newId } : msg))
            );
          }
        }
        if (abortControllerRef.current?.signal.aborted) {
          break;
        }

        const delta = chunk.choices[0]?.delta;
        if (delta) {
          const rc =
            (delta as any).reasoning_content ||
            (delta as any).thinking ||
            (delta as any).reasoning;
          if (typeof rc === 'string' && rc.length > 0) {
            updateQueue.push(`<think>${rc}</think>`);
          }
          if (typeof delta.content === 'string' && delta.content.length > 0) {
            updateQueue.push(delta.content);
          }
          if (updateQueue.length > 0) updateUI();
        }

        if (chunk.choices[0]?.finish_reason) {
          break;
        }
      }

      // Flush any remaining content
      if (updateQueue.length > 0) {
        updateUI();
      }

      // Save history when streaming completes successfully
      try {
        await chatService.saveChatHistory('models-gallery', chatId, messages);
      } catch (error) {
        console.error('Failed to save chat history after completion:', error);
      }
    } catch (error: any) {
      console.error('Chat completion error:', error);
      let errorMessage = 'Failed to send message';
      if (error instanceof Error) {
        if (error.message.includes('MetaMask wallet address is required')) {
          errorMessage = 'Please connect your MetaMask wallet to continue.';
        } else {
          errorMessage = error.message;
        }
      }
      setError(errorMessage);

      // Remove the empty assistant message on error
      setMessages(prev => prev.filter(msg => msg.id !== assistantMessage.id));

      // Save history even on error/interruption (with current messages)
      try {
        await chatService.saveChatHistory('models-gallery', chatId, messages);
      } catch (saveError) {
        console.error('Failed to save chat history after error:', saveError);
      }
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  const handleStopGeneration = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsLoading(false);
      setIsStreaming(false);

      // Save history when user stops generation
      try {
        await chatService.saveChatHistory('models-gallery', chatId, messages);
      } catch (error) {
        console.error('Failed to save chat history after stop:', error);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    setMessages([]);
    setError(null);
  };

  return (
    <div className={styles.chatInterface}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.modelInfo}>
          <h2 className={styles.modelName}>{modelName}</h2>
          <span className={styles.modelId}>Model: {modelId}</span>
        </div>
        <div className={styles.headerActions}>
          <Button
            variant="secondary"
            size="sm"
            onClick={async () => {
              console.log('Testing API...');
              const result = await testChatAPI();
              if (result) {
                alert('API test successful! Check console for details.');
              } else {
                alert('API test failed! Check console for errors.');
              }
            }}
          >
            Test API
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setInputValue('Hello! Can you hear me?');
            }}
          >
            Add Test Message
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={async () => {
              console.log('🏠 Address update feature');
              alert(
                'Address update available via CLI:\n\nnpm run update:addresses\nnpm run update:addresses:check\nnpm run watch:addresses\n\nSee console for more details.'
              );
              console.log('📋 Available address update commands:');
              console.log(
                '  npm run update:addresses        - Update addresses from GitHub'
              );
              console.log(
                '  npm run update:addresses:check  - Check for address changes'
              );
              console.log(
                '  npm run watch:addresses         - Watch for address changes'
              );
            }}
          >
            🏠 Update Addresses
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleClearChat}
            disabled={messages.length === 0}
          >
            Clear Chat
          </Button>
          {onBack && (
            <Button variant="secondary" size="sm" onClick={onBack}>
              ← Back to Models
            </Button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className={styles.messagesContainer}>
        {messages.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>💬</div>
            <h3>Start a conversation</h3>
            <p>Send a message to begin chatting with {modelName}</p>
          </div>
        ) : (
          <div className={styles.messagesList}>
            {messages.map(message => (
              <div
                key={message.id}
                className={`${styles.message} ${styles[message.role]}`}
              >
                <div className={styles.messageHeader}>
                  <span className={styles.messageRole}>
                    {message.role === 'user' ? 'You' : modelName}
                  </span>
                  {message.timestamp && (
                    <span className={styles.messageTime}>
                      {message.timestamp.toLocaleTimeString()}
                    </span>
                  )}
                </div>
                <div className={styles.messageContent}>
                  {message.role === 'assistant' ? (
                    (() => {
                      const modelForParse =
                        typeof (message as any)?.model === 'string'
                          ? ((message as any).model as string)
                          : ((message as any)?.model?.id ||
                            (message as any)?.model?.name ||
                            '') as string;
                      const parsed = parseThinkingFromText(message.content, {
                        model: modelForParse,
                      });
                      return (
                        <>
                          <ThinkingPanel blocks={parsed.thinking} />
                          {parsed.content}
                        </>
                      );
                    })()
                  ) : (
                    message.content
                  )}
                  {isStreaming &&
                    message.role === 'assistant' &&
                    messages.indexOf(message) === messages.length - 1 && (
                      <span className={styles.cursor}>|</span>
                    )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className={styles.errorMessage}>
          <span className={styles.errorIcon}>⚠️</span>
          <span>{error}</span>
          <button
            className={styles.errorDismiss}
            onClick={() => setError(null)}
          >
            ×
          </button>
        </div>
      )}

      {/* Input */}
      <div className={styles.inputContainer}>
        <div className={styles.inputWrapper}>
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Message ${modelName}...`}
            className={styles.messageInput}
            rows={1}
            disabled={isLoading}
          />
          <div className={styles.inputActions}>
            {isLoading ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleStopGeneration}
                className={styles.stopButton}
              >
                Stop
              </Button>
            ) : (
              <Button
                variant="primary"
                size="sm"
                onClick={handleSendMessage}
                disabled={!inputValue.trim()}
                className={styles.sendButton}
              >
                Send
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
